<template>
  <div class="coach-report-ai-helper" style="display:flex; gap:6px; flex-wrap:wrap; margin-bottom:6px;">
    <!-- IMPORTANT iOS Safari note:
         Use a NATIVE <button>, NOT <b-button>. Bootstrap-Vue 2.x running on
         Vue 3 via @vue/compat introduces just enough indirection in the click
         event path (b-button + child-component boundary) to drop iOS Safari's
         "user activation" context — which SpeechRecognition.start() requires.
         A direct native button keeps the gesture context intact. Bootstrap
         CSS classes give the same visual result. Do not "tidy this up" by
         switching back to b-button. -->
    <button class="btn btn-outline-danger btn-sm voice-record-button" type="button"
      @click="toggleVoiceRecording">
      <i :class="`bi ${isRecording ? 'bi-mic-fill' : 'bi-mic'}`" aria-hidden="true"></i>
      {{ isRecording ? "Stop Recording" : "Record Voice" }}
    </button>
    <!-- "Refine with AI" doesn't need a user gesture, so b-button is safe here.
         But keeping it as native button for visual consistency with the mic. -->
    <button class="btn btn-outline-primary btn-sm" type="button"
      :disabled="refining || !hasText"
      @click="refineText">
      <span v-if="refining">Refining…</span>
      <span v-else><i class="bi bi-stars"></i> Refine with AI</span>
    </button>
  </div>
</template>

<script>
import { AI } from "@/services/ai-service.js";

export default {
  name: "CoachReportAIHelper",
  props: {
    // Vue 3 v-model name (preferred).
    modelValue: { type: String, default: undefined },
    // Vue 2 v-model name — accepted as fallback for @vue/compat parents
    // whose v-model may have compiled to the Vue 2 binding shape.
    value: { type: String, default: undefined },
    fieldType: {
      type: String,
      required: true,
      validator: (v) => ["positives", "improvements"].includes(v),
    },
    playerName: { type: String, default: "" },
    position: { type: String, default: "" },
  },
  emits: ["update:modelValue", "input", "transcript"],
  data() {
    return {
      isRecording: false,
      recognition: null,
      refining: false,
      // Snapshot of the parent's value when recording starts — see the
      // interimResults comment in startVoiceRecording below.
      baseValue: "",
    };
  },
  computed: {
    // Single source of truth — prefers Vue 3 prop.
    currentValue() {
      return this.modelValue !== undefined ? this.modelValue : this.value;
    },
    hasText() {
      return !!(this.currentValue && this.currentValue.trim());
    },
  },
  beforeUnmount() {
    // Modal close → component unmount → must release the mic, otherwise
    // iOS Safari may refuse the next session.
    this.teardownRecognition();
  },
  methods: {
    // Hard teardown — see VoiceDictationButton for the full iOS Safari
    // rationale. Null every handler, stop+abort, drop the reference.
    teardownRecognition() {
      const r = this.recognition;
      if (!r) return;
      console.log("[CoachReportAI] teardownRecognition");
      try { r.onresult = null; } catch (e) { /* ignore */ }
      try { r.onend = null; } catch (e) { /* ignore */ }
      try { r.onerror = null; } catch (e) { /* ignore */ }
      try { r.onstart = null; } catch (e) { /* ignore */ }
      try { r.onnomatch = null; } catch (e) { /* ignore */ }
      try { r.onspeechend = null; } catch (e) { /* ignore */ }
      try { r.stop(); } catch (e) { /* ignore */ }
      try { r.abort(); } catch (e) { /* ignore */ }
      this.recognition = null;
    },
    toggleVoiceRecording() {
      if (this.isRecording) this.stopVoiceRecording();
      else this.startVoiceRecording();
    },
    startVoiceRecording() {
      console.log("[CoachReportAI] startVoiceRecording, isRecording was:", this.isRecording, "had old recognition:", !!this.recognition);
      this.teardownRecognition();

      const SpeechRecognition =
        window.SpeechRecognition || window.webkitSpeechRecognition;
      if (!SpeechRecognition) {
        alert("Your browser does not support voice recording. On iPhone, use Safari 14.5 or newer.");
        return;
      }

      let recognition;
      try {
        recognition = new SpeechRecognition();
        console.log("[CoachReportAI] new SpeechRecognition() ok");
      } catch (err) {
        console.error("[CoachReportAI] SpeechRecognition constructor failed:", err);
        alert("Voice recording could not start: " + (err.message || err));
        return;
      }

      recognition.lang = "en-UK";
      // CRITICAL: interimResults: true. iOS Safari discards the audio
      // buffer on .stop() after the first session — interim events give
      // us the text as it's spoken, before .stop() runs. See
      // VoiceDictationButton for the full explanation.
      recognition.interimResults = true;
      recognition.maxAlternatives = 1;
      recognition.continuous = false;

      this.baseValue = this.currentValue || "";

      recognition.onstart = () => {
        console.log("[CoachReportAI] onstart fired — mic is live");
      };
      recognition.onspeechstart = () => {
        console.log("[CoachReportAI] onspeechstart — speech detected");
      };
      recognition.onspeechend = () => {
        console.log("[CoachReportAI] onspeechend — speech ended");
      };
      recognition.onresult = (event) => {
        try {
          let transcriptSoFar = "";
          for (let i = 0; i < event.results.length; i++) {
            transcriptSoFar += event.results[i][0].transcript;
          }
          transcriptSoFar = transcriptSoFar.trim();
          const isFinal = event.results[event.results.length - 1]?.isFinal;
          console.log(
            "[CoachReportAI] onresult fired, isFinal:", isFinal,
            "transcript:", transcriptSoFar,
          );
          if (!transcriptSoFar) return;

          const next =
            `${this.baseValue}${this.baseValue ? " " : ""}${transcriptSoFar}`;

          this.$emit("update:modelValue", next);
          this.$emit("input", next);
          this.$emit("transcript", transcriptSoFar);

          this.$nextTick(() => {
            this.$emit("update:modelValue", next);
            this.$emit("input", next);
          });
        } catch (err) {
          console.error("[CoachReportAI] onresult handler threw:", err);
        }
      };
      recognition.onend = () => {
        console.log("[CoachReportAI] onend fired");
        this.isRecording = false;
        this.teardownRecognition();
      };
      recognition.onnomatch = () => {
        console.log("[CoachReportAI] onnomatch fired — no recognisable speech");
      };
      recognition.onerror = (event) => {
        console.log("[CoachReportAI] onerror fired:", event.error);
        this.isRecording = false;
        const err = event.error;
        this.teardownRecognition();
        if (err === "not-allowed" || err === "service-not-allowed") {
          alert(
            "Microphone access is blocked. On iPhone: Settings → Safari → Microphone, allow access for this site, then reload.",
          );
        } else if (err === "no-speech" || err === "aborted") {
          // Common, no alert
        } else if (err === "audio-capture") {
          alert("No microphone was found on this device.");
        } else if (err === "network") {
          alert("A network error occurred. Voice recognition needs an internet connection.");
        } else {
          console.error("[CoachReportAI] unhandled SpeechRecognition error:", err);
          alert("Voice recording error: " + err);
        }
      };

      this.recognition = recognition;
      this.isRecording = true;

      // Defer .start() by one tick so iOS can release the audio session
      // from the previous instance.
      setTimeout(() => {
        if (this.recognition !== recognition) {
          console.log("[CoachReportAI] start() skipped — recognition was replaced");
          return;
        }
        try {
          recognition.start();
          console.log("[CoachReportAI] recognition.start() called");
        } catch (err) {
          this.isRecording = false;
          this.teardownRecognition();
          console.error("[CoachReportAI] recognition.start() threw:", err);
          alert("Voice recording could not start: " + (err.message || err));
        }
      }, 0);
    },
    stopVoiceRecording() {
      console.log("[CoachReportAI] stopVoiceRecording");
      if (this.recognition) {
        try { this.recognition.stop(); } catch (e) { /* ignore */ }
      }
      this.isRecording = false;
    },
    async refineText() {
      if (!this.hasText) {
        alert("Please dictate or type some text first, then click Refine.");
        return;
      }
      this.refining = true;
      try {
        const response = await AI.find({
          action: "refineCoachReportText",
          field_type: this.fieldType,
          raw_text: this.currentValue,
          player_name: this.playerName,
          position: this.position,
        });
        const data = response?.data;
        if (data?.error) {
          alert("AI refine failed: " + data.error);
          return;
        }
        if (data?.refused) {
          alert(
            "The AI only helps with match-report feedback for this player. Please keep your notes focused on how the player performed in this match.",
          );
          return;
        }
        if (data?.refined) {
          this.$emit("update:modelValue", data.refined);
          this.$emit("input", data.refined);
          this.$nextTick(() => {
            this.$emit("update:modelValue", data.refined);
            this.$emit("input", data.refined);
          });
        } else {
          alert("AI returned an unexpected response. Please try again.");
        }
      } catch (err) {
        console.error(err);
        alert("AI refine request failed. Please try again.");
      } finally {
        this.refining = false;
      }
    },
  },
};
</script>
